import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { Control } from "react-hook-form";
import { Controller } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import Close from "~/components/Icons/Close";
import Button from "~/shared/ui/Fields/Button";
import { NOTIFICATIONS } from "~/constans/notifications";
import { uploadFile } from "~/libs/uploadFile";
import { useAddNotificationMutation } from "~/schemas/generated/graphql";
import { Field } from "~/shared/ui/Field";

import s from "~/components/Forms/CreateDocument/CreateDocument.less";

interface IFilePicker {
  control: Control<any>;
  name: string;
  label: string;
  isEdit: boolean;
  error?: string;
  disabled?: boolean;
}

const FilePicker = ({
  control,
  name,
  label,
  isEdit,
  error,
  disabled,
}: IFilePicker) => {
  const { t } = useTranslation();
  return (
    <Controller
      control={control}
      name={name}
      render={FilePickerRaw(
        name,
        control,
        label,
        isEdit,
        error,
        disabled,
        t,
        t("documents.chooseFiles"),
        t("documents.loadMore")
      )}
    />
  );
};

export default FilePicker;

const FilePickerRaw =
  (name, control, label, isEdit, error, disabled, t, chooseFilesText, loadMoreText) => (props) => {
    const { value, onChange } = props;

    const [isEditHide, setIsEditHide] = useState(isEdit || !!value?.length);
    const [hidePicker, setHidePicker] = useState(false);
    const [manualPickerShow, setManualPickerShow] = useState(false);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);

    const [addNotificationMutation] = useAddNotificationMutation({});

    const fallBack = () => {
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.error,
          content: t("common.data_uploaded_error"),
        },
      });
    };

    useEffect(() => {
      if (files?.length) {
        onChange(files);

        control.defaultValuesRef.current.filesIds = files;
      } else {
        onChange(undefined);
      }
    }, [files]);

    useEffect(() => {
      if (value?.length) {
        if (!files.length) setFiles(value);

        if (!manualPickerShow) {
          setHidePicker(true);
          setIsEditHide(true);
        }
      } else {
        setHidePicker(false);
        setIsEditHide(false);
      }
    }, [value]);

    const showPicker = () => {
      setIsEditHide(false);
      setManualPickerShow(true);
      setHidePicker(false);
    };
    const deleteFile = (deletingId: number) => () =>
      setFiles((prev) => prev.filter((el) => el.id !== deletingId));

    return (
      <div className={s.filePicker}>
        {/*<span className={s.title}>{label}</span>*/}
        <div className={s.filePickerWrapper}>
          {Array.isArray(files) && !!files?.length && (
            <div className={s.loadedFiles}>
              {files.map((el) => (
                <div key={el.id} className={s.loadedFilesItem}>
                  <span>{el.fileName}</span>
                  {!disabled && (
                    <Button onlyIcon onClick={deleteFile(el.id)}>
                      <Close fill={"#000"} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
          {!!error && <span className={s.error_text}>{error}</span>}
          {hidePicker && !disabled && (
            <div className={s.loadMore} onClick={showPicker}>
              {loadMoreText}
            </div>
          )}
        </div>
        <div
          className={clsx(s.filePickerContainer, {
            [s.hide]: hidePicker,
            [s.error]: !!error,
            [s.disabled]: !!disabled,
          })}
        >
          {!isEditHide && (
            <Field
              type="file"
              name={`${name}Picker`}
              control={control}
              loading={loading}
              disabled={disabled}
              onUpload={async (file) => {
                setLoading(true);
                try {
                  const uploaded = await uploadFile(file, fallBack);
                  if (uploaded) {
                    // FileField однофайловый: id добавляем в общий список
                    // (onChange сработает через useEffect по files), а onUpload
                    // ничего не возвращает — пикер остаётся пустым.
                    setFiles((p) => [
                      ...p,
                      { id: uploaded.id, fileName: uploaded.name },
                    ]);
                    addNotificationMutation({
                      variables: {
                        type: NOTIFICATIONS.success,
                        content: t("common.file_uploaded_successfully"),
                      },
                    });
                  }
                } catch (error) {
                  if (error.response?.data?.message === "File too large") {
                    addNotificationMutation({
                      variables: {
                        type: NOTIFICATIONS.error,
                        content: t("common.data_uploaded_error_size"),
                      },
                    });
                  } else {
                    fallBack();
                  }
                } finally {
                  setLoading(false);
                }
              }}
            />
          )}
        </div>
      </div>
    );
  };
